import axios from "axios";
import {
  ILocation,
  ICoordinates,
  IGetLocationsParams,
  ILocationFinderService,
} from "./location-finder.service";

// const locationsURL = "/mock-content/api/mock-data-location-updated.json";

/*

DO NOT DELETE: http://localhost:4502/libs/cq/search/content/querydebug.html

path=/content/arbormemorial/ca/en
type=cq:Page
1_property.property=jcr:content/cq:template
1_property.value=/conf/arbormemorial/settings/wcm/templates/branch-page-template
2_property.property=jcr:content/city
2_property.value=Toronto
3_property.property=jcr:content/province
3_property.value=ON
4_property.property=jcr:content/cq:tags
4_property.value=branchType
5_rangeproperty.property=jcr:content/capacity
5_rangeproperty.lowerBound=0
5_rangeproperty.lowerOperation=>=
5_rangeproperty.upperBound=50
5_rangeproperty.upperOperation=<=
p.properties=address1,address2,city,province,postal,latitude,longitude,branchLocationId
*/

const buildParameters = (parameters) => {
  const {
    searchedData: {
      branchName,
      city,
      province,
      freeFormSearchTerm,
      latitude,
      longitude,
    },
    branchIds,
    capacity,
  } = parameters;

  let validProperties: { name: string; value: any }[] = [];
  if (city) validProperties.push({ name: "city", value: city.trim() });
  if (province)
    validProperties.push({ name: "province", value: province.trim() });

  if (branchName)
    validProperties.push({
      name: "branchLocationName",
      value: branchName,
    });

  if (branchIds.length)
    validProperties.push({ name: "branchType", value: branchIds });
  if (capacity["minCapacity"] || capacity["maxCapacity"]) {
    validProperties.push({ name: "minCapacity", value: capacity.minCapacity });
    validProperties.push({ name: "maxCapacity", value: capacity.maxCapacity });
  }

  if (latitude && !freeFormSearchTerm)
    validProperties.push({ name: "latitude", value: latitude });
  if (longitude && !freeFormSearchTerm)
    validProperties.push({ name: "longitude", value: longitude });

  if (freeFormSearchTerm)
    validProperties.push({ name: "freeFormSearch", value: freeFormSearchTerm });

  return validProperties.reduce((acc, current, index) => {
    const propertyIndex = index + 2;
    if (current.name === "city") {
      return `${acc}&${propertyIndex}_property.property=jcr:content/city&${propertyIndex}_property.value=${encodeURIComponent(
        current.value
      )}`;
    } else if (current.name === "province") {
      return `${acc}&${propertyIndex}_property.property=jcr:content/province&${propertyIndex}_property.value=${encodeURIComponent(
        current.value
      )}`;
    } else if (current.name === "branchLocationName") {
      return `${acc}&${propertyIndex}_property.property=jcr:content/branchLocationName&${propertyIndex}_property.value=${encodeURIComponent(
        current.value
      )}`;
    } else if (current.name === "branchType") {
      const groupProps = current.value
        .map(
          (el, idx) =>
            `&${propertyIndex}_group.1_group.${
              idx + 1
            }_property.property=jcr:content/cq:tags&${propertyIndex}_group.1_group.${
              idx + 1
            }_property.value=${encodeURIComponent(el)}`
        )
        .join("");
      return `${acc}&${propertyIndex}_group.1_group.p.or=true${groupProps}`;
    } else if (current.name === "minCapacity") {
      return `${acc}&${propertyIndex}_rangeproperty.property=jcr:content/capacity&${propertyIndex}_rangeproperty.lowerBound=${
        !current.value ? 0 : current.value
      }&${propertyIndex}_rangeproperty.lowerOperation=>=`;
    } else if (current.name === "maxCapacity") {
      return !!current.value
        ? `${acc}&${propertyIndex - 1}_rangeproperty.upperBound=${
            current.value
          }&${propertyIndex - 1}_rangeproperty.upperOperation=<=`
        : acc;
    } else if (current.name === "longitude") {
      const lngLowerBound = Number(longitude) - 0.5;
      const lngUpperBound = Number(longitude) + 0.5;
      return `${acc}&${propertyIndex}_rangeproperty.property=jcr:content/longitude&${propertyIndex}_rangeproperty.lowerBound=${lngLowerBound}&${propertyIndex}_rangeproperty.upperBound=${lngUpperBound}&${propertyIndex}_rangeproperty.decimal=true`;
    } else if (current.name === "latitude") {
      const latLowerBound = Number(latitude) - 0.5;
      const latUpperBound = Number(latitude) + 0.5;
      return `${acc}&${propertyIndex}_rangeproperty.property=jcr:content/latitude&${propertyIndex}_rangeproperty.lowerBound=${latLowerBound}&${propertyIndex}_rangeproperty.upperBound=${latUpperBound}&${propertyIndex}_rangeproperty.decimal=true`;
    } else if (current.name === "freeFormSearch") {
      const groupProps = [
        "branchLocationName",
        "city",
        "province",
        "jcr:title",
        "branchKeywords",
      ]
        .map(
          (el, idx) =>
            `&${propertyIndex}_group.1_group.${
              idx + 1
            }_likeIgnoreCase.property=jcr:content/${el}&${propertyIndex}_group.1_group.${
              idx + 1
            }_likeIgnoreCase.value=%25${encodeURIComponent(
              current.value
            )}%25&${propertyIndex}_group.1_group.${
              idx + 1
            }_likeIgnoreCase.operation=like`
        )
        .join("");
      return `${acc}&${propertyIndex}_group.1_group.p.or=true${groupProps}`;
    } else {
      return acc;
    }
  }, "");
};

const mergeFrSearchResults = (
  resultsEn: ILocation[],
  resultsFr: ILocation[]
) => {
  // Merges French and English search results
  // by replacing English results with corresponding French ones
  // based on matching URLs, if available.
  const frenchifiedEnResults = resultsEn.map((resultEn) => {
    const enUrl = resultEn.url.split("/").pop();
    const matchingResultFr = resultsFr.find((resultFr) => {
      const frUrl = resultFr.url.split("/").pop();
      return enUrl === frUrl;
    });

    if (matchingResultFr) {
      resultEn = JSON.parse(JSON.stringify(matchingResultFr));
    }

    return resultEn;
  });

  const mergedResults = [
    ...resultsFr.filter(
      ({ url }) => !frenchifiedEnResults.some((result) => result.url === url)
    ),
    ...frenchifiedEnResults,
  ];

  return mergedResults;
};

const mergeFrSearchSuggestions = (
  resultsEn: ILocation[],
  resultsFr: ILocation[]
) => {
  const urlMap = new Map();
  resultsFr.forEach((object) => {
    const lastPath = object.url.split("/").pop();
    urlMap.set(lastPath, object);
  });

  const mergedResults = resultsEn
    .filter((object) => {
      const lastPath = object.url.split("/").pop();
      return !urlMap.has(lastPath);
    })
    .concat(resultsFr);

  return mergedResults;
};

const calculateDistance = (
  location1: ICoordinates,
  location2: ICoordinates
) => {
  const toRadians = (degree: number) => {
    return degree * (Math.PI / 180);
  };

  const R = 6371e3; // earth radius in meters
  const lat1 = toRadians(location1.lat);
  const lat2 = toRadians(location2.lat);
  const deltaLat = toRadians(location2.lat - location1.lat);
  const deltaLong = toRadians(location2.lng - location1.lng);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLong / 2) *
      Math.sin(deltaLong / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const sortLocationsByDistance = (
  searchLocation: ICoordinates,
  locations: ILocation[]
) => {
  return locations.sort((a, b) => {
    const { latitude: lat1, longitude: lng1 } = a.customProperties;
    const { latitude: lat2, longitude: lng2 } = b.customProperties;
    const location1 = { lat: Number(lat1), lng: Number(lng1) };
    const location2 = { lat: Number(lat2), lng: Number(lng2) };
    const distanceA = calculateDistance(searchLocation, location1);
    const distanceB = calculateDistance(searchLocation, location2);

    return distanceA - distanceB;
  });
};

const sortLocationsByName = (locations: ILocation[]) => {
  return locations.sort((a, b) => {
    const { branchLocationName: branchNameA } = a.customProperties;
    const { branchLocationName: branchNameB } = b.customProperties;
    const nameA = branchNameA?.toLowerCase() || a?.title?.toLowerCase() || "";
    const nameB = branchNameB?.toLowerCase() || b?.title?.toLowerCase() || "";

    return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
  });
};

const sortResults = (results: ILocation[], params: IGetLocationsParams) => {
  const { latitude, longitude } = params.searchedData;
  if (latitude && longitude) {
    const searchCoordinates = { lat: Number(latitude), lng: Number(longitude) };
    return sortLocationsByDistance(searchCoordinates, results);
  } else {
    return sortLocationsByName(results);
  }
};

const LocationFinderService: ILocationFinderService = {
  searchLocations: async (itemsPath: string, params) => {
    try {
      const pathEn = itemsPath.replace("ca/fr", "ca/en");
      const isEnglish = itemsPath.includes("ca/en");

      const resDataEn = await axios.get(
        `${pathEn}.branches.json?path=/content/arbormemorial/ca/en&type=cq:Page&1_property.property=jcr:content/cq:template&1_property.value=/conf/arbormemorial/settings/wcm/templates/branch-page-template${buildParameters(
          params
        )}&p.properties=address,address1,address2,capacity,city,province,postalcode,latitude,longitude,branchLocationId,branchLocationName&p.offset=0&p.limit=200`
      );

      if (!isEnglish) {
        const resDataFr = await axios.get(
          `${pathEn}.branches.json?path=/content/arbormemorial/ca/fr&type=cq:Page&1_property.property=jcr:content/cq:template&1_property.value=/conf/arbormemorial/settings/wcm/templates/branch-page-template${buildParameters(
            params
          )}&p.properties=address,address1,address2,capacity,city,province,postalcode,latitude,longitude,branchLocationId,branchLocationName&p.offset=0&p.limit=200`
        );

        const mergedResults = mergeFrSearchResults(
          resDataEn.data.results,
          resDataFr.data.results
        );

        return sortResults(mergedResults, params);
      } else {
        return sortResults(resDataEn.data.results, params);
      }
    } catch (err) {
      throw err;
    }
  },

  getBranchDropdownOptions: async (itemsPath: string, params) => {
    try {
      const pathEn = itemsPath.replace("ca/fr", "ca/en");
      const isEnglish = itemsPath.includes("ca/en");

      const resDataEn = await axios.get(
        `${pathEn}.branches.json?path=/content/arbormemorial/ca/en&type=cq:Page&1_property.property=jcr:content/cq:template&1_property.value=/conf/arbormemorial/settings/wcm/templates/branch-page-template&2_likeIgnoreCase.property=jcr:content/@${
          Object.keys(params)[0]
        }&2_likeIgnoreCase.value=%25${
          Object.values(params)[0]
        }%25&2_likeIgnoreCase.operation=like&p.properties=address,address1,address2,capacity,city,province,postalcode,latitude,longitude,branchLocationId,branchLocationName&p.offset=0&p.limit=500`
      );

      if (!isEnglish) {
        const resDataFr = await axios.get(
          `${pathEn}.branches.json?path=/content/arbormemorial/ca/fr&type=cq:Page&1_property.property=jcr:content/cq:template&1_property.value=/conf/arbormemorial/settings/wcm/templates/branch-page-template&2_likeIgnoreCase.property=jcr:content/@${
            Object.keys(params)[0]
          }&2_likeIgnoreCase.value=%25${
            Object.values(params)[0]
          }%25&2_likeIgnoreCase.operation=like&p.properties=address,address1,address2,capacity,city,province,postalcode,latitude,longitude,branchLocationId,branchLocationName&p.offset=0&p.limit=500`
        );

        const mergedResults = mergeFrSearchSuggestions(
          resDataEn.data.results,
          resDataFr.data.results
        );

        return mergedResults;
      } else {
        return resDataEn.data.results;
      }
    } catch (err) {
      throw err;
    }
  },

  getCityDropdownOptions: async (itemsPath: string, params) => {
    try {
      const pathEn = itemsPath.replace("ca/fr", "ca/en");
      const resData = await axios.get(
        `${pathEn}.branches.json?path=/content/arbormemorial/ca/en&type=cq:Page&1_property.property=jcr:content/cq:template&1_property.value=/conf/arbormemorial/settings/wcm/templates/branch-page-template&2_likeIgnoreCase.property=jcr:content/@${
          Object.keys(params)[0]
        }&2_likeIgnoreCase.value=%25${
          Object.values(params)[0]
        }%25&2_likeIgnoreCase.operation=like&p.properties=address,address1,address2,capacity,city,province,postalcode,latitude,longitude,branchLocationId,branchLocationName&p.offset=0&p.limit=500`
      );
      return resData.data.results;
    } catch (err) {
      throw err;
    }
  },
};

export default LocationFinderService;
